import { IVisitorCompany } from '@app/src/components/event-management/model/VisitorCompany';
import {
  CheckedInType,
  CheckedOutType
} from '@app/src/components/event-management/model/VisitHistory';
import { PreApprovalStatus } from '@app/src/components/event-management/model/PreApproval';

export interface IVisitor {
  id: string;
  visitorTypeId: string;
  externalId: string;
  staticPin: string;
  email: string;
  firstName: string;
  lastName: string;
  description: string;
  phone: string;
  address: string;
  companyName: string;
  visitorCompanyId?: string;
  company?: string;
  gender: VisitorGender;
  mailLanguage: string;
  notificationSent: boolean;
  checkedIn: boolean;
  fullName: string;
  photoFileId: string;
  position: string;
  dateOfBirth: string;
  licensePlates?: string[];
  blocked: boolean;
  visitorDeleted: boolean;
  companyId?: string; // platform/tenant company id
  assets?: { [key: string]: string };
}

export interface IVisitorFormObject {
  id?: string;
  externalId: string;
  staticPin: string;
  firstName: string;
  lastName: string;
  description: string;
  phone: string;
  address: string;
  companyName: string;
  visitorCompanyId: string | null;
  email: string | null;
  mailLanguage: string;
  gender: VisitorGender;
  visitorId: string;
  photoFileId: string | File | null;
  position: string;
  dateOfBirth: string | null;
  companyId?: string; // platform/tenant company id
}

export interface IEventVisitor {
  id?: string;
  eventId: string;
  visitorId: string;
  visitorRootId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  companyName: string;
  email: string;
  visitorCompany: IVisitorCompany | null;
  notificationSent: boolean;
  groupNotificationSent: boolean;
  selfRegistration: boolean;
  blocked: boolean;
  onSite: boolean;
  visitorDeleted: boolean;
  company: string | null;
  pinCode: string;

  registrationDate?: string;
  checkedInDate: string;
  checkInType: CheckedInType;
  checkedInByFullName: string | null;
  checkedOutDate: string | null;
  checkOutType: CheckedOutType;
  checkedOutBy: string | null;
  checkedOutByFullName: string | null;
  parkingBookingExists?: boolean;
  parkingBookingValidationStatus?: number;
  preApprovalStatus: PreApprovalStatus;
  canReopen: boolean;
}

export enum Attendance {
  Attending = 0,
  Declined = 1
}

export interface IEventVisitorProperties {
  licensePlate?: string;
  attendance: Attendance;
  canBookParking: boolean;
}

export interface IVisitorListItem {
  id: string;
  rootId: string;
  email: string;
  firstName: string;
  lastName: string;
  description: string;
  phone: string;
  companyName: string;
  fullName: string;
  blocked: boolean;
  companyId?: string;
  platformCompanyName?: string;
  assets?: { [key: string]: string };
}

export interface IVisitorSearchResponse {
  eventId: string;
  visitorId: string;
}

export interface ICheckInResult {
  eventId: string;
  visitorId: string;
  checkInId: string;
  checkedIn: boolean;
  errorCode?: string;
  message?: string;
}

export interface ISiportCheckInResult {
  success: boolean;
  errorCode?: string | null;
  errorDetail?: string | null;
}

export interface ISiportCheckOutCardResult extends ISiportCheckInResult {
  visitFound: boolean;
  checkInId?: string | null;
}

export enum CheckInType {
  ExternalId,
  Pin,
  EventIdVisitorId,
  CardId,
  EventVisitorPin,
  VisitorStaticPin,
  ExternalCode
}

export interface IMatrixCheckInCardResult {
  success: boolean;
  matrixApplied: boolean;
  permissionsGranted?: boolean | null;
  permissionsDetail?: string | null;
  errorCode?: string | null;
  errorDetail?: string | null;
}

export interface ICheckOutResult {
  eventId: string;
  visitorId: string;
  checkedOut: boolean;
  errorCode?: string;
  message?: string;
  matrixWarning?: string | null;
}

export interface IVisitorBadgeResponse {
  errorMessage: string | null;
  badgeText: string | null;
}

export enum VisitorSearchCodeType {
  qrCode = 2,
  pinCode = 1,
  barCode = 1,
  rfId = 3,
  externalId = 0
}

export enum VisitorGender {
  MALE = 1,
  FEMALE = 2,
  DIVERSE = 3
}

export enum DocumentType {
  AGREEMENT = 0,
  QUESTIONNAIRE = 1
}

export interface IVisitorDocument {
  id: string;
  fileId: string;
  name: string;
  downloadUrl: string;
  type: DocumentType;
  createdDate: string;
  modifiedDate?: Date;
  modifiedBy?: string;
  hasHistory: boolean;
}

export interface IVisitorDocumentHistory {
  createdDate: Date;
  createdBy: string;
  fileId: string;
  downloadUrl: string;
}

export interface IVisitorDocumentWithHistory extends IVisitorDocument {
  documents?: IVisitorDocumentHistory[];
}

export interface IVisitorType {
  id: string;
  name: string;
  description: string;
}
